<template>
  <div
    ref="rootEl"
    id="markdown-renderer"
    class="md-content"
    data-component-id="md--renderer--root"
    v-html="renderedHtml"
    @click="onClick"
    @mouseover="onHoverIn"
    @mouseout="onHoverOut"
  ></div>

  <!-- Lightbox fullscreen untuk diagram (mermaid) & chart (echarts/dashboard) -->
  <Teleport to="body">
    <div
      v-if="lightbox.open"
      class="md-lightbox"
      @wheel.prevent="onWheel"
      @pointerdown="onPanStart"
      @pointermove="onPanMove"
      @pointerup="onPanEnd"
      @click.self="onBackdropClick"
    >
      <div class="md-lightbox-toolbar" @pointerdown.stop @click.stop>
        <button type="button" aria-label="Perkecil" title="Perkecil" @click="zoomBy(-0.2)">−</button>
        <span class="md-lightbox-zoom">{{ Math.round(zoom * 100) }}%</span>
        <button type="button" aria-label="Perbesar" title="Perbesar" @click="zoomBy(0.2)">+</button>
        <button type="button" class="md-lightbox-reset" aria-label="Reset" title="Reset tampilan" @click="resetView">Reset</button>
        <button type="button" class="md-lightbox-close" aria-label="Tutup" title="Tutup (Esc)" @click="closeLightbox">✕</button>
      </div>
      <div ref="lightboxBody" class="md-lightbox-body" :style="bodyStyle"></div>
      <div class="md-lightbox-hint">Scroll untuk zoom · seret untuk menggeser · Esc untuk menutup</div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { marked } from 'marked'
import { renderMermaid, nextMermaidId } from '../composables/useMermaid'
import { renderEChart, renderDashboard } from '../composables/useECharts'

const props = defineProps({
  content:    { type: String,  default: '' },
  streaming:  { type: Boolean, default: false },
  glossaryMap: { type: Object, default: null }, // { term: { definition, example, category } }
})
const emit = defineEmits(['gloss-click', 'gloss-hover', 'gloss-leave'])

marked.setOptions({
  breaks: true,
  gfm: true,
})

const rootEl = ref(null)

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const renderer = new marked.Renderer()
renderer.code = function (codeOrToken, infostringMaybe) {
  let code = ''
  let info = ''
  if (typeof codeOrToken === 'object' && codeOrToken !== null) {
    code = codeOrToken.text || ''
    info = codeOrToken.lang || ''
  } else {
    code = codeOrToken || ''
    info = infostringMaybe || ''
  }
  const lang = (info || '').split(/\s+/)[0] || ''

  // Mermaid: emit a placeholder; we render SVG asynchronously after streaming completes.
  // The original source is escaped and stashed in data-mermaid-source so we can re-attempt
  // render or fall back to code-block on parse error.
  if (lang === 'mermaid') {
    const safeSrc = escapeHtml(code)
    return (
      `<div class="md-mermaid" data-mermaid data-mermaid-source="${safeSrc}">` +
      `  <div class="md-mermaid-placeholder">` +
      `    <span class="md-mermaid-spin"></span>` +
      `    <span>Memuat diagram…</span>` +
      `  </div>` +
      `</div>`
    )
  }

  // ECharts chart / dashboard: same lazy pattern as mermaid — emit placeholder,
  // render after streaming completes. Source (JSON) stashed escaped.
  if (lang === 'echarts' || lang === 'dashboard' || lang === 'ga4-dashboard') {
    const kind = lang === 'echarts' ? 'echarts' : 'dashboard'
    const safeSrc = escapeHtml(code)
    return (
      `<div class="md-chart" data-chart="${kind}" data-chart-source="${safeSrc}">` +
      `  <div class="md-chart-placeholder">` +
      `    <span class="md-mermaid-spin"></span>` +
      `    <span>Memuat ${kind === 'echarts' ? 'chart' : 'dashboard'}…</span>` +
      `  </div>` +
      `</div>`
    )
  }

  const safe = escapeHtml(code)
  const langLabel = lang ? escapeHtml(lang) : 'text'
  return (
    `<div class="md-code-block">` +
    `  <div class="md-code-head">` +
    `    <span class="md-code-lang">${langLabel}</span>` +
    `    <button type="button" class="md-code-copy" data-md-copy aria-label="Salin kode">` +
    `      <span class="md-code-copy-icon">📋</span>` +
    `      <span class="md-code-copy-label">Salin</span>` +
    `    </button>` +
    `  </div>` +
    `  <pre><code${lang ? ` class="language-${lang}"` : ''}>${safe}</code></pre>` +
    `</div>`
  )
}

const renderedHtml = computed(() => {
  if (!props.content) return ''
  return marked.parse(props.content, { renderer })
})

function decodeMermaidSource(node) {
  // The HTML attribute value is already decoded by the browser when read via
  // getAttribute. We still escape on output, so this just returns the source.
  return node.getAttribute('data-mermaid-source') || ''
}

async function processMermaidBlocks() {
  // Skip during streaming — partial mermaid syntax crashes the parser. We'll
  // render after the stream completes.
  if (props.streaming) return
  const root = rootEl.value
  if (!root) return
  const blocks = root.querySelectorAll('[data-mermaid]:not([data-mermaid-rendered])')
  if (!blocks.length) return

  for (const block of blocks) {
    block.setAttribute('data-mermaid-rendered', 'pending')
    const src = decodeMermaidSource(block)
    try {
      const id = nextMermaidId()
      const svg = await renderMermaid(id, src)
      if (svg) {
        block.innerHTML = svg
        block.setAttribute('data-mermaid-rendered', 'ok')
        addExpandButton(block)
      } else {
        renderMermaidFallback(block, src)
      }
    } catch {
      renderMermaidFallback(block, src)
    }
  }
}

function renderMermaidFallback(block, src) {
  const safe = escapeHtml(src)
  block.innerHTML =
    `<div class="md-code-block md-mermaid-fallback">` +
    `  <div class="md-code-head">` +
    `    <span class="md-code-lang">mermaid (gagal render)</span>` +
    `    <button type="button" class="md-code-copy" data-md-copy aria-label="Salin kode">` +
    `      <span class="md-code-copy-icon">📋</span>` +
    `      <span class="md-code-copy-label">Salin</span>` +
    `    </button>` +
    `  </div>` +
    `  <pre><code class="language-mermaid">${safe}</code></pre>` +
    `</div>`
  block.setAttribute('data-mermaid-rendered', 'fail')
}

// ── ECharts chart / dashboard blocks ──────────────────────────────────────────
// Mirror mermaid: render after streaming. ECharts instances are tracked so we can
// dispose them when the message re-renders (v-html swap) or the component unmounts.
const chartHandles = []

function disposeChart(h) {
  try { h.chart?.dispose() } catch { /* already disposed */ }
  try { h.ro?.disconnect() } catch { /* no-op */ }
}

function disposeDetachedCharts() {
  for (let i = chartHandles.length - 1; i >= 0; i--) {
    const h = chartHandles[i]
    if (!rootEl.value || !rootEl.value.contains(h.el)) {
      disposeChart(h)
      chartHandles.splice(i, 1)
    }
  }
}

async function processChartBlocks() {
  if (props.streaming) return // partial JSON breaks JSON.parse — wait for full stream
  const root = rootEl.value
  if (!root) return
  disposeDetachedCharts()
  const blocks = root.querySelectorAll('[data-chart]:not([data-chart-rendered])')
  if (!blocks.length) return

  for (const block of blocks) {
    block.setAttribute('data-chart-rendered', 'pending')
    const kind = block.getAttribute('data-chart')
    const src = block.getAttribute('data-chart-source') || ''
    let spec
    try { spec = JSON.parse(src) } catch { renderChartFallback(block, src, kind); continue }
    try {
      if (kind === 'echarts') {
        let option = spec, height
        if (spec && spec.option && !spec.series) { option = spec.option; height = spec.height }
        block.innerHTML = ''
        const inner = document.createElement('div')
        block.appendChild(inner)
        const h = await renderEChart(inner, option, { height })
        chartHandles.push({ el: block, ...h })
      } else {
        const hs = await renderDashboard(block, spec)
        hs.forEach(h => chartHandles.push({ el: block, ...h }))
      }
      block.setAttribute('data-chart-rendered', 'ok')
      addExpandButton(block)
    } catch {
      renderChartFallback(block, src, kind)
    }
  }
}

// ── Fullscreen lightbox ───────────────────────────────────────────────────────
// Tombol "perbesar" disuntik ke tiap diagram/chart yang sukses render. Klik →
// buka overlay layar-penuh: mermaid di-clone SVG-nya, chart/dashboard di-render
// ulang dari sumber JSON yang masih tersimpan di data-chart-source.

const EXPAND_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>' +
  '<line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>'

function addExpandButton(block) {
  if (block.querySelector(':scope > .md-expand-btn')) return
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'md-expand-btn'
  btn.setAttribute('data-md-expand', '')
  btn.setAttribute('aria-label', 'Perbesar (layar penuh)')
  btn.title = 'Perbesar (layar penuh)'
  btn.innerHTML = EXPAND_ICON
  block.appendChild(btn)
}

const lightbox = ref({ open: false })
const lightboxBody = ref(null)
const lightboxHandles = []
const zoom = ref(1)
const pan = ref({ x: 0, y: 0 })
let panning = false
let panned = false
let panOrigin = { x: 0, y: 0 }

const bodyStyle = computed(() => ({
  transform: `translate(${pan.value.x}px, ${pan.value.y}px) scale(${zoom.value})`,
}))

function disposeLightboxCharts() {
  for (const h of lightboxHandles) disposeChart(h)
  lightboxHandles.length = 0
}

async function openLightbox(block) {
  const isChart = block.matches('[data-chart]')
  resetView()
  lightbox.value = { open: true }
  await nextTick()
  const body = lightboxBody.value
  if (!body) return
  disposeLightboxCharts()
  body.innerHTML = ''

  if (!isChart) {
    const svg = block.querySelector('svg')
    if (!svg) return
    const clone = svg.cloneNode(true)
    clone.removeAttribute('width')
    clone.removeAttribute('height')
    clone.style.maxWidth = '90vw'
    clone.style.maxHeight = '84vh'
    clone.style.width = 'auto'
    clone.style.height = 'auto'
    body.appendChild(clone)
    return
  }

  const kind = block.getAttribute('data-chart')
  const src = block.getAttribute('data-chart-source') || ''
  let spec
  try { spec = JSON.parse(src) } catch { return }
  if (kind === 'echarts') {
    let option = spec
    if (spec && spec.option && !spec.series) option = spec.option
    const inner = document.createElement('div')
    inner.style.width = '86vw'
    body.appendChild(inner)
    const h = await renderEChart(inner, option, { height: Math.round(window.innerHeight * 0.78) })
    lightboxHandles.push(h)
  } else {
    const holder = document.createElement('div')
    holder.style.width = '90vw'
    body.appendChild(holder)
    const hs = await renderDashboard(holder, spec)
    hs.forEach(h => lightboxHandles.push(h))
  }
}

function closeLightbox() {
  disposeLightboxCharts()
  lightbox.value = { open: false }
  resetView()
}

function resetView() {
  zoom.value = 1
  pan.value = { x: 0, y: 0 }
}

function clampZoom(z) {
  return Math.min(6, Math.max(0.3, +z.toFixed(2)))
}

function zoomBy(delta) {
  zoom.value = clampZoom(zoom.value + delta)
}

function onWheel(e) {
  zoomBy(e.deltaY < 0 ? 0.18 : -0.18)
}

function onPanStart(e) {
  panning = true
  panned = false
  panOrigin = { x: e.clientX - pan.value.x, y: e.clientY - pan.value.y }
}

function onPanMove(e) {
  if (!panning) return
  const x = e.clientX - panOrigin.x
  const y = e.clientY - panOrigin.y
  if (Math.abs(x - pan.value.x) > 2 || Math.abs(y - pan.value.y) > 2) panned = true
  pan.value = { x, y }
}

function onPanEnd() {
  panning = false
}

function onBackdropClick() {
  // Klik area kosong (bukan setelah drag) → tutup
  if (!panned) closeLightbox()
}

function onKeydown(e) {
  if (e.key === 'Escape' && lightbox.value.open) closeLightbox()
}

function renderChartFallback(block, src, kind) {
  const safe = escapeHtml(src)
  block.innerHTML =
    `<div class="md-code-block md-chart-fallback">` +
    `  <div class="md-code-head">` +
    `    <span class="md-code-lang">${kind === 'echarts' ? 'echarts' : 'dashboard'} (JSON tidak valid)</span>` +
    `    <button type="button" class="md-code-copy" data-md-copy aria-label="Salin kode">` +
    `      <span class="md-code-copy-icon">📋</span>` +
    `      <span class="md-code-copy-label">Salin</span>` +
    `    </button>` +
    `  </div>` +
    `  <pre><code>${safe}</code></pre>` +
    `</div>`
  block.setAttribute('data-chart-rendered', 'fail')
}

function onClick(e) {
  // Expand button — buka diagram/chart layar penuh
  const expandBtn = e.target.closest('[data-md-expand]')
  if (expandBtn) {
    e.preventDefault()
    e.stopPropagation()
    const block = expandBtn.closest('[data-mermaid], [data-chart]')
    if (block) openLightbox(block)
    return
  }

  // Glossary term click — emit event with term and anchor rect
  const termSpan = e.target.closest('.gloss-term')
  if (termSpan) {
    e.preventDefault()
    e.stopPropagation()
    cancelHoverTimer()
    emit('gloss-click', {
      term: termSpan.dataset.term,
      rect: termSpan.getBoundingClientRect(),
    })
    return
  }

  const btn = e.target.closest('[data-md-copy]')
  if (!btn) return
  e.preventDefault()
  e.stopPropagation()
  const wrap = btn.closest('.md-code-block')
  const code = wrap?.querySelector('pre code')
  if (!code) return
  const text = code.innerText
  navigator.clipboard?.writeText(text).then(() => {
    const label = btn.querySelector('.md-code-copy-label')
    const icon  = btn.querySelector('.md-code-copy-icon')
    const prevLabel = label?.textContent
    const prevIcon  = icon?.textContent
    if (label) label.textContent = 'Tersalin'
    if (icon)  icon.textContent  = '✓'
    btn.classList.add('md-code-copy-ok')
    setTimeout(() => {
      if (label) label.textContent = prevLabel || 'Salin'
      if (icon)  icon.textContent  = prevIcon  || '📋'
      btn.classList.remove('md-code-copy-ok')
    }, 1600)
  }).catch(() => { /* clipboard blocked */ })
}

// ── Glossary hover (delay 350ms supaya tidak terlalu sensitif) ────────────────
const HOVER_OPEN_DELAY = 350
const HOVER_CLOSE_DELAY = 200
let hoverOpenTimer = null
let hoverCloseTimer = null
let hoverActiveTerm = null

function cancelHoverTimer() {
  if (hoverOpenTimer)  { clearTimeout(hoverOpenTimer);  hoverOpenTimer = null }
  if (hoverCloseTimer) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null }
}

function onHoverIn(e) {
  const termSpan = e.target.closest?.('.gloss-term')
  if (!termSpan) return
  if (hoverCloseTimer) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null }
  if (hoverActiveTerm === termSpan.dataset.term) return
  if (hoverOpenTimer) clearTimeout(hoverOpenTimer)
  hoverOpenTimer = setTimeout(() => {
    hoverActiveTerm = termSpan.dataset.term
    emit('gloss-hover', {
      term: termSpan.dataset.term,
      rect: termSpan.getBoundingClientRect(),
    })
  }, HOVER_OPEN_DELAY)
}

function onHoverOut(e) {
  const termSpan = e.target.closest?.('.gloss-term')
  if (!termSpan) return
  // Pastikan benar-benar keluar dari span (bukan move ke child)
  const related = e.relatedTarget
  if (related && termSpan.contains(related)) return
  if (hoverOpenTimer) { clearTimeout(hoverOpenTimer); hoverOpenTimer = null }
  if (hoverCloseTimer) clearTimeout(hoverCloseTimer)
  hoverCloseTimer = setTimeout(() => {
    hoverActiveTerm = null
    emit('gloss-leave')
  }, HOVER_CLOSE_DELAY)
}

// ── Glossary Term Highlighting ────────────────────────────────────────────────
// Runs setelah streaming selesai AND glossaryMap siap. Walks semua text node
// (skip code/pre/script/style/anchor) dan bungkus istilah yang cocok dengan
// .gloss-term span. Boleh re-apply: dipanggil ulang setelah v-html re-render
// karena content berubah, atau saat glossaryMap dapat term baru.

let lastAppliedSignature = ''

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function makeSignature(content, map) {
  const mapKey = map ? Object.keys(map).sort().join('|') : ''
  return `${(content || '').length}::${mapKey}`
}

function processGlossaryTerms() {
  if (!rootEl.value || props.streaming) return
  const map = props.glossaryMap
  if (!map) return
  const terms = Object.keys(map).filter(t => t.length >= 2)
  if (!terms.length) return

  const signature = makeSignature(props.content, map)
  if (signature === lastAppliedSignature) {
    // DOM sudah punya .gloss-term yang sesuai signature ini → skip walk ulang.
    // Tapi hanya kalau memang masih ada di DOM (kalau v-html barusan reset, perlu re-walk).
    if (rootEl.value.querySelector('.gloss-term')) return
  }

  // Build lowercase → canonical-term lookup so matched text di DOM (case bebas)
  // bisa di-resolve balik ke key di map saat user klik.
  const canonicalByLower = new Map()
  for (const t of terms) canonicalByLower.set(t.toLowerCase(), t)

  // Sort longest first to avoid partial matches (ROAS before R)
  const sorted = [...terms].sort((a, b) => b.length - a.length)
  // Single capturing group — split gives: [text, match, text, match, ...]
  // Odd indices are matched terms, even indices are plain text.
  const regex = new RegExp(`\\b(${sorted.map(escapeRegex).join('|')})\\b`, 'gi')

  const walker = document.createTreeWalker(rootEl.value, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let p = node.parentElement
      while (p && p !== rootEl.value) {
        if (['CODE', 'PRE', 'SCRIPT', 'STYLE', 'A'].includes(p.tagName)) return NodeFilter.FILTER_REJECT
        if (p.classList?.contains('gloss-term')) return NodeFilter.FILTER_REJECT
        p = p.parentElement
      }
      return NodeFilter.FILTER_ACCEPT
    },
  })

  const textNodes = []
  let node
  while ((node = walker.nextNode())) textNodes.push(node)

  for (const textNode of textNodes) {
    const text = textNode.textContent
    if (!regex.test(text)) { regex.lastIndex = 0; continue }
    regex.lastIndex = 0

    const parts = text.split(regex)
    if (parts.length <= 1) continue

    const fragment = document.createDocumentFragment()
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      if (!part) continue
      if (i % 2 === 1) {
        // Odd index = captured group = matched term (case bebas dari teks asli)
        const canonical = canonicalByLower.get(part.toLowerCase()) || part
        const span = document.createElement('span')
        span.className = 'gloss-term'
        span.dataset.term = canonical
        span.dataset.match = part
        span.textContent = part
        fragment.appendChild(span)
      } else {
        fragment.appendChild(document.createTextNode(part))
      }
    }
    textNode.parentNode.replaceChild(fragment, textNode)
  }

  lastAppliedSignature = signature
}

// Re-apply ketika: streaming selesai, content berubah (v-html re-render),
// atau glossaryMap dapat term baru. Signature-check di processGlossaryTerms
// mencegah pekerjaan ulang yang tidak perlu.
watch(
  () => ({
    streaming: props.streaming,
    contentLen: (props.content || '').length,
    mapSize: Object.keys(props.glossaryMap || {}).length,
  }),
  ({ streaming, mapSize }) => {
    if (!streaming && mapSize > 0) {
      nextTick(processGlossaryTerms)
    }
  },
  { immediate: true },
)

function processVisualBlocks() {
  processMermaidBlocks()
  processChartBlocks()
}

watch(() => [props.content, props.streaming], () => nextTick(processVisualBlocks), { immediate: false })
onMounted(() => {
  nextTick(processVisualBlocks)
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  for (const h of chartHandles) disposeChart(h)
  chartHandles.length = 0
  disposeLightboxCharts()
  window.removeEventListener('keydown', onKeydown)
})
</script>

<style>
.md-content .md-code-block {
  position: relative;
  margin: 8px 0;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.08)));
}
.md-content .md-code-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 10px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.08)));
  font-size: 10.5px;
  color: var(--text-dim, #6b7a8d);
}
.md-content .md-code-lang {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  font-family: 'Consolas', 'Monaco', monospace;
}
.md-content .md-code-copy {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--text-dim, #6b7a8d);
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10.5px;
  font-family: inherit;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.md-content .md-code-copy:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-base, var(--text-body));
  border-color: var(--border-soft, var(--border-light));
}
.md-content .md-code-copy.md-code-copy-ok {
  color: var(--accent-green, #22c55e);
  border-color: rgba(34, 197, 94, 0.4);
}
.md-content .md-code-block pre {
  margin: 0;
  padding: 9px 11px;
  background: transparent;
  overflow-x: auto;
}
.md-content .md-code-block pre code {
  background: transparent;
  padding: 0;
  font-size: 12px;
  line-height: 1.5;
  font-family: 'Consolas', 'Monaco', monospace;
}

/* ── Mermaid diagram ───────────────────────────────────────────────────── */
.md-content .md-mermaid {
  position: relative;
  margin: 10px 0;
  padding: 12px;
  border-radius: 8px;
  background: var(--bg-base, var(--bg-deep, #0a0e17));
  border: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.08)));
  text-align: center;
  overflow-x: auto;
}
.md-content .md-mermaid svg {
  max-width: 100%;
  height: auto;
  display: inline-block;
}
.md-content .md-mermaid-placeholder {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 11.5px;
  color: var(--text-dim, #6b7a8d);
}
.md-content .md-mermaid-spin {
  width: 11px; height: 11px; border-radius: 50%;
  border: 2px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.12)));
  border-top-color: var(--accent, var(--accent-blue, #3b82f6));
  animation: md-mermaid-spin 0.9s linear infinite;
}
@keyframes md-mermaid-spin { to { transform: rotate(360deg); } }
.md-content .md-mermaid[data-mermaid-rendered="ok"] {
  padding: 14px;
  background: linear-gradient(135deg, var(--bg-base, #0a0e17), rgba(59, 130, 246, 0.04));
}
.md-content .md-mermaid-fallback {
  margin: 0 !important;
}

/* ── ECharts chart & dashboard ─────────────────────────────────────────────── */
.md-content .md-chart {
  position: relative;
  margin: 10px 0;
  padding: 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--bg-base, #0a0e17), rgba(59, 130, 246, 0.04));
  border: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.08)));
  overflow: hidden;
}
.md-content .md-chart-placeholder {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 11.5px;
  color: var(--text-dim, #6b7a8d);
}
.md-content .md-chart-fallback { margin: 0 !important; }

/* Dashboard: judul + KPI cards + grid chart */
.md-content .md-dash-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text-base, var(--text-body, #e2e8f0));
  margin: 2px 2px 12px;
  letter-spacing: 0.2px;
}
.md-content .md-dash-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.md-content .md-dash-kpi {
  padding: 12px 14px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.07)));
}
.md-content .md-dash-kpi-val {
  font-size: 20px;
  font-weight: 750;
  line-height: 1.1;
  color: var(--text-base, #e2e8f0);
  font-variant-numeric: tabular-nums;
}
.md-content .md-dash-kpi-label {
  margin-top: 3px;
  font-size: 11px;
  color: var(--text-dim, #6b7a8d);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.md-content .md-dash-kpi-delta {
  margin-top: 6px;
  display: inline-block;
  font-size: 11px;
  font-weight: 650;
  padding: 1px 7px;
  border-radius: 99px;
}
.md-content .md-dash-kpi-delta.is-up   { color: #34d399; background: rgba(16, 185, 129, 0.14); }
.md-content .md-dash-kpi-delta.is-down { color: #f87171; background: rgba(239, 68, 68, 0.14); }
.md-content .md-dash-kpi-delta.is-flat { color: var(--text-dim, #94a3b8); background: rgba(148, 163, 184, 0.12); }
.md-content .md-dash-charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.md-content .md-dash-chart {
  min-width: 0;
  padding: 10px 8px 4px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.06)));
}
.md-content .md-dash-chart.is-full { grid-column: 1 / -1; }
.md-content .md-dash-chart-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-base, #cbd5e1);
  margin: 0 4px 4px;
}
.md-content .md-dash-chart-canvas { width: 100%; }
@media (max-width: 560px) {
  .md-content .md-dash-charts { grid-template-columns: 1fr; }
}

/* ── Expand button (perbesar diagram/chart) ───────────────────────────────── */
.md-content .md-expand-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 7px;
  background: rgba(13, 18, 32, 0.72);
  border: 1px solid var(--border-soft, var(--border-light, rgba(255,255,255,0.12)));
  color: var(--text-dim, #94a3b8);
  cursor: pointer;
  opacity: 0;
  transform: translateY(-2px);
  transition: opacity 0.15s, color 0.15s, background 0.15s, border-color 0.15s, transform 0.15s;
  backdrop-filter: blur(6px);
}
.md-content .md-mermaid:hover .md-expand-btn,
.md-content .md-chart:hover .md-expand-btn {
  opacity: 1;
  transform: translateY(0);
}
.md-content .md-expand-btn:hover {
  color: #e2e8f0;
  background: rgba(59, 130, 246, 0.28);
  border-color: rgba(59, 130, 246, 0.5);
}
/* Layar sentuh: tak ada hover → tampilkan samar agar tetap kelihatan */
@media (hover: none) {
  .md-content .md-expand-btn { opacity: 0.85; }
}

/* ── Glossary Terms ─────────────────────────────────────────────────────────── */
.md-content .gloss-term {
  font-weight: 700;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: color-mix(in srgb, var(--accent-blue, #3b82f6) 55%, transparent);
  text-underline-offset: 3px;
  color: var(--accent-blue-light, #7db4f5);
  cursor: pointer;
  border-radius: 2px;
  transition: background 0.12s, color 0.12s;
  padding: 0 1px;
}
.md-content .gloss-term:hover {
  background: color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, transparent);
  color: var(--accent-blue-light, #93c5fd);
}

/* ── Lightbox fullscreen (teleported ke body, di luar .md-content) ───────────── */
.md-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(5, 8, 14, 0.86);
  backdrop-filter: blur(8px);
  cursor: grab;
  touch-action: none;
  animation: md-lightbox-in 0.18s ease;
}
.md-lightbox:active { cursor: grabbing; }
@keyframes md-lightbox-in { from { opacity: 0; } to { opacity: 1; } }

.md-lightbox-body {
  transform-origin: center center;
  background: #0a0e17;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
  will-change: transform;
}
.md-lightbox-body svg { display: block; }

.md-lightbox-toolbar {
  position: absolute;
  top: 14px;
  right: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 10px;
  background: rgba(13, 18, 32, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
}
.md-lightbox-toolbar button {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 7px;
  border: 1px solid transparent;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}
.md-lightbox-toolbar button:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }
.md-lightbox-toolbar .md-lightbox-reset { font-size: 11.5px; font-weight: 600; }
.md-lightbox-toolbar .md-lightbox-close { color: #f87171; }
.md-lightbox-toolbar .md-lightbox-close:hover { background: rgba(239, 68, 68, 0.2); color: #fca5a5; }
.md-lightbox-zoom {
  font-size: 11.5px;
  color: #94a3b8;
  min-width: 42px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.md-lightbox-hint {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: rgba(203, 213, 225, 0.55);
  background: rgba(13, 18, 32, 0.6);
  padding: 5px 12px;
  border-radius: 99px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  pointer-events: none;
  white-space: nowrap;
}
</style>
